import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { SortHeader, makeToggleSort, cmp, type SortState } from '@/components/ui/sort-header';
import { api } from '@/lib/api';

interface AuditLogItem {
  id: string;
  action: string;
  entity: string | null;
  entityId: string | null;
  ip: string | null;
  createdAt: string;
  user: { id: string; name: string; email: string } | null;
  before?: unknown;
  after?: unknown;
  metadata?: unknown;
}

interface AuditResponse {
  items: AuditLogItem[];
  total: number;
}

type AuditSortColumn = 'createdAt' | 'user' | 'action' | 'entity' | 'ip';

// ---------------------------------------------------------------------------
// Tradução de termos técnicos para linguagem do dia a dia.
// Os valores das chaves são exatamente os mesmos que o backend grava no banco
// (ver backend/src/**/*.service.ts), então ao filtrar enviamos o código cru e
// só substituímos pelo texto amigável na hora de exibir.
// ---------------------------------------------------------------------------

const ACTION_LABELS: Record<string, string> = {
  // Acesso
  LOGIN: 'Entrou no sistema',
  LOGOUT: 'Saiu do sistema',
  // Usuários
  USER_CREATE: 'Cadastrou usuário',
  USER_UPDATE: 'Editou usuário',
  USER_PASSWORD_CHANGE: 'Trocou senha',
  USER_DEACTIVATE: 'Desativou usuário',
  // Contas bancárias
  ACCOUNT_CREATE: 'Cadastrou conta bancária',
  ACCOUNT_UPDATE: 'Editou conta bancária',
  // Plano de contas
  CHART_CREATE: 'Criou plano de contas',
  CHART_UPDATE: 'Editou plano de contas',
  CHART_DELETE: 'Excluiu plano de contas',
  // Centro de custo
  COST_CENTER_CREATE: 'Criou centro de custo',
  COST_CENTER_UPDATE: 'Editou centro de custo',
  // Clientes / Fornecedores
  PARTY_CREATE: 'Cadastrou cliente/fornecedor',
  PARTY_UPDATE: 'Editou cliente/fornecedor',
  PARTY_DELETE: 'Excluiu cliente/fornecedor',
  // Movimentações
  TRANSACTION_CREATE: 'Lançou movimentação',
  TRANSACTION_REVERSE: 'Estornou movimentação',
  TRANSACTION_RECONCILE: 'Conciliou movimentação',
  // Transferências
  TRANSFER_CREATE: 'Criou transferência',
  TRANSFER_UPDATE: 'Editou transferência',
  TRANSFER_DELETE: 'Excluiu transferência',
  // Contas a pagar
  PAYABLE_CREATE: 'Lançou conta a pagar',
  PAYABLE_INSTALLMENTS_CREATED: 'Gerou parcelas a pagar',
  PAYABLE_UPDATE: 'Editou conta a pagar',
  PAYABLE_PAY: 'Pagou conta',
  PAYABLE_CANCEL: 'Cancelou conta a pagar',
  PAYABLE_DELETE: 'Excluiu conta a pagar',
  // Contas a receber
  RECEIVABLE_CREATE: 'Lançou conta a receber',
  RECEIVABLE_INSTALLMENTS_CREATED: 'Gerou parcelas a receber',
  RECEIVABLE_UPDATE: 'Editou conta a receber',
  RECEIVABLE_RECEIVE: 'Recebeu conta',
  RECEIVABLE_CANCEL: 'Cancelou conta a receber',
  RECEIVABLE_DELETE: 'Excluiu conta a receber',
  // Recorrências
  RECURRING_CREATE: 'Criou recorrência',
  RECURRING_UPDATE: 'Editou recorrência',
  RECURRING_DELETE: 'Excluiu recorrência',
  RECURRING_GENERATE_NEXT: 'Gerou próximas recorrências',
  RECURRING_GENERATE_ONE: 'Gerou recorrência avulsa',
  // Manutenção (backup / limpeza)
  'maintenance.export': 'Exportou backup',
  'maintenance.import': 'Importou backup',
  'maintenance.clear': 'Zerou base de dados',
};

const ENTITY_LABELS: Record<string, string> = {
  User: 'Usuário',
  Account: 'Conta bancária',
  ChartOfAccount: 'Plano de contas',
  CostCenter: 'Centro de custo',
  Party: 'Cliente/Fornecedor',
  Transaction: 'Movimentação',
  Transfer: 'Transferência',
  Payable: 'Conta a pagar',
  Receivable: 'Conta a receber',
  RecurringRule: 'Recorrência',
};

// Agrupa as ações por área para o dropdown ficar organizado.
const ACTION_GROUPS: { label: string; actions: string[] }[] = [
  { label: 'Acesso', actions: ['LOGIN', 'LOGOUT'] },
  { label: 'Usuários', actions: ['USER_CREATE', 'USER_UPDATE', 'USER_PASSWORD_CHANGE', 'USER_DEACTIVATE'] },
  { label: 'Contas bancárias', actions: ['ACCOUNT_CREATE', 'ACCOUNT_UPDATE'] },
  { label: 'Plano de contas', actions: ['CHART_CREATE', 'CHART_UPDATE', 'CHART_DELETE'] },
  { label: 'Centro de custo', actions: ['COST_CENTER_CREATE', 'COST_CENTER_UPDATE'] },
  { label: 'Clientes/Fornecedores', actions: ['PARTY_CREATE', 'PARTY_UPDATE', 'PARTY_DELETE'] },
  { label: 'Movimentações', actions: ['TRANSACTION_CREATE', 'TRANSACTION_REVERSE', 'TRANSACTION_RECONCILE'] },
  { label: 'Transferências', actions: ['TRANSFER_CREATE', 'TRANSFER_UPDATE', 'TRANSFER_DELETE'] },
  {
    label: 'Contas a pagar',
    actions: [
      'PAYABLE_CREATE',
      'PAYABLE_INSTALLMENTS_CREATED',
      'PAYABLE_UPDATE',
      'PAYABLE_PAY',
      'PAYABLE_CANCEL',
      'PAYABLE_DELETE',
    ],
  },
  {
    label: 'Contas a receber',
    actions: [
      'RECEIVABLE_CREATE',
      'RECEIVABLE_INSTALLMENTS_CREATED',
      'RECEIVABLE_UPDATE',
      'RECEIVABLE_RECEIVE',
      'RECEIVABLE_CANCEL',
      'RECEIVABLE_DELETE',
    ],
  },
  {
    label: 'Recorrências',
    actions: [
      'RECURRING_CREATE',
      'RECURRING_UPDATE',
      'RECURRING_DELETE',
      'RECURRING_GENERATE_NEXT',
      'RECURRING_GENERATE_ONE',
    ],
  },
  {
    label: 'Manutenção',
    actions: ['maintenance.export', 'maintenance.import', 'maintenance.clear'],
  },
];

const ENTITY_OPTIONS: { value: string; label: string }[] = Object.entries(ENTITY_LABELS)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

function actionLabel(code: string): string {
  return ACTION_LABELS[code] ?? code;
}

function entityLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return ENTITY_LABELS[code] ?? code;
}

// ---------------------------------------------------------------------------
// Resumo do que foi afetado (texto que aparece no tooltip do #idCurto).
// O backend grava o snapshot em `after` (e em `before` quando faz update/delete).
// Pegamos o que está mais "completo" disponível e montamos uma frase humana.
// ---------------------------------------------------------------------------

function isObj(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

function pickSnapshot(it: AuditLogItem): Record<string, unknown> | null {
  if (isObj(it.after) && Object.keys(it.after).length > 0) return it.after;
  if (isObj(it.before) && Object.keys(it.before).length > 0) return it.before;
  return null;
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function fmtMoney(v: unknown): string {
  if (v == null) return '';
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN;
  if (!Number.isFinite(n)) return '';
  return BRL.format(n);
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
}

const PARTY_TYPE_LABEL: Record<string, string> = {
  CUSTOMER: 'Cliente',
  SUPPLIER: 'Fornecedor',
  EMPLOYEE: 'Funcionário',
  CONTRACTOR: 'Prestador',
};

const DIRECTION_LABEL: Record<string, string> = {
  IN: 'Entrada',
  OUT: 'Saída',
};

const STATUS_LABEL: Record<string, string> = {
  // Payable
  OPEN: 'Em aberto',
  PARTIALLY_PAID: 'Parcialmente paga',
  PAID: 'Paga',
  CANCELED: 'Cancelada',
  // Receivable
  PARTIALLY_RECEIVED: 'Parcialmente recebida',
  RECEIVED: 'Recebida',
  // Transaction
  CONFIRMED: 'Confirmada',
  REVERSED: 'Estornada',
  RECONCILED: 'Conciliada',
  // Recurring
  ACTIVE: 'Ativa',
  PAUSED: 'Pausada',
  ENDED: 'Encerrada',
};

function joinNonEmpty(parts: (string | undefined | null | false)[], sep = ' • '): string {
  return parts.filter(Boolean).join(sep);
}

/**
 * Gera um resumo legível do que foi afetado, para mostrar como tooltip.
 * Retorna string vazia se não houver dados úteis (deixa o tooltip de fora).
 */
function summarizeEntry(it: AuditLogItem): string {
  // Ações de manutenção têm só metadata.
  if (it.action.startsWith('maintenance.') && isObj(it.metadata)) {
    const m = it.metadata as Record<string, unknown>;
    if (it.action === 'maintenance.export' || it.action === 'maintenance.clear') {
      const lines = [
        m.users != null && `${m.users} usuário(s)`,
        m.accounts != null && `${m.accounts} conta(s) bancária(s)`,
        m.transactions != null && `${m.transactions} movimentação(ões)`,
        m.payables != null && `${m.payables} conta(s) a pagar`,
        m.receivables != null && `${m.receivables} conta(s) a receber`,
        m.recurringRules != null && `${m.recurringRules} recorrência(s)`,
      ].filter(Boolean) as string[];
      return lines.join('\n');
    }
    if (it.action === 'maintenance.import' && isObj(m.summary)) {
      const s = m.summary as Record<string, unknown>;
      return [
        `${s.users ?? 0} usuário(s)`,
        `${s.accounts ?? 0} conta(s) bancária(s)`,
        `${s.transactions ?? 0} movimentação(ões)`,
        `${s.payables ?? 0} conta(s) a pagar`,
        `${s.receivables ?? 0} conta(s) a receber`,
        `${s.recurringRules ?? 0} recorrência(s)`,
      ].join('\n');
    }
  }

  // Para parcelas geradas em lote, o metadata já carrega o resumo.
  if (
    (it.action === 'PAYABLE_INSTALLMENTS_CREATED' ||
      it.action === 'RECEIVABLE_INSTALLMENTS_CREATED') &&
    isObj(it.metadata)
  ) {
    const m = it.metadata as Record<string, unknown>;
    const head = `${m.count ?? '?'} parcela(s) — total ${fmtMoney(m.totalAmount)}`;
    const snap = pickSnapshot(it);
    if (snap?.description) return joinNonEmpty([head, String(snap.description)], '\n');
    return head;
  }

  const snap = pickSnapshot(it);
  if (!snap) return '';

  switch (it.entity) {
    case 'User':
      return joinNonEmpty([
        snap.name ? String(snap.name) : '',
        snap.email ? `<${snap.email}>` : '',
        snap.role ? `Perfil: ${snap.role}` : '',
        snap.active === false ? 'Inativo' : '',
      ]);

    case 'Account':
      return joinNonEmpty([
        snap.name ? String(snap.name) : '',
        snap.type ? String(snap.type) : '',
        snap.bankCode ? `Banco ${snap.bankCode}` : '',
        snap.agency || snap.accountNumber
          ? `Ag ${snap.agency ?? '?'} / Cc ${snap.accountNumber ?? '?'}`
          : '',
      ]);

    case 'ChartOfAccount':
      return joinNonEmpty([
        snap.code && snap.name ? `${snap.code} — ${snap.name}` : (snap.name as string) ?? '',
        snap.nature ? String(snap.nature) : '',
      ]);

    case 'CostCenter':
      return joinNonEmpty([
        snap.code && snap.name ? `${snap.code} — ${snap.name}` : (snap.name as string) ?? '',
        snap.active === false ? 'Inativo' : '',
      ]);

    case 'Party':
      return joinNonEmpty([
        snap.code && snap.name ? `${snap.code} — ${snap.name}` : (snap.name as string) ?? '',
        snap.type ? PARTY_TYPE_LABEL[String(snap.type)] ?? String(snap.type) : '',
        snap.document ? `Doc ${snap.document}` : '',
      ]);

    case 'RecurringRule':
      return joinNonEmpty([
        snap.description ? String(snap.description) : '',
        DIRECTION_LABEL[String(snap.direction)] ?? '',
        fmtMoney(snap.amount),
        snap.dayOfMonth ? `todo dia ${snap.dayOfMonth}` : '',
        STATUS_LABEL[String(snap.status)] ?? '',
      ]);

    case 'Payable':
    case 'Receivable': {
      const paidOrReceived =
        it.entity === 'Payable'
          ? fmtMoney(snap.paidAmount)
          : fmtMoney(snap.receivedAmount);
      const statusLabel = STATUS_LABEL[String(snap.status)] ?? String(snap.status ?? '');
      const installments =
        snap.installmentNumber && snap.installmentTotal
          ? `Parcela ${snap.installmentNumber}/${snap.installmentTotal}`
          : '';
      return joinNonEmpty([
        snap.description ? String(snap.description) : '',
        fmtMoney(snap.amount),
        snap.dueDate ? `Vence ${fmtDate(snap.dueDate)}` : '',
        statusLabel,
        installments,
        paidOrReceived && paidOrReceived !== 'R$ 0,00'
          ? `${it.entity === 'Payable' ? 'Pago' : 'Recebido'}: ${paidOrReceived}`
          : '',
        snap.reference ? `Ref ${snap.reference}` : '',
      ]);
    }

    case 'Transaction':
      return joinNonEmpty([
        DIRECTION_LABEL[String(snap.direction)] ?? '',
        snap.description ? String(snap.description) : '',
        fmtMoney(snap.amount),
        snap.date ? fmtDate(snap.date) : '',
        STATUS_LABEL[String(snap.status)] ?? '',
        snap.reference ? `Ref ${snap.reference}` : '',
      ]);

    case 'Transfer':
      return joinNonEmpty([
        fmtMoney(snap.amount),
        snap.date ? fmtDate(snap.date) : '',
        snap.description ? String(snap.description) : '',
      ]);

    default:
      return '';
  }
}

export function AuditLogsPage() {
  const [filters, setFilters] = useState({ action: '', entity: '' });
  const [skip, setSkip] = useState(0);
  const take = 50;
  const [sort, setSort] = useState<SortState<AuditSortColumn>>({ column: 'createdAt', dir: 'desc' });
  const toggleSort = makeToggleSort(setSort);

  const { data, isFetching } = useQuery({
    queryKey: ['audit-logs', filters, skip],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.action) params.set('action', filters.action);
      if (filters.entity) params.set('entity', filters.entity);
      params.set('skip', String(skip));
      params.set('take', String(take));
      return api<AuditResponse>(`/audit-logs?${params.toString()}`);
    },
  });

  const sortedItems = useMemo(() => {
    const rows = data?.items ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let primary = 0;
      switch (sort.column) {
        case 'createdAt': primary = cmp.date(a.createdAt, b.createdAt); break;
        case 'user':      primary = cmp.text(a.user?.name ?? '', b.user?.name ?? ''); break;
        case 'action':    primary = cmp.text(actionLabel(a.action), actionLabel(b.action)); break;
        case 'entity':    primary = cmp.text(entityLabel(a.entity), entityLabel(b.entity)); break;
        case 'ip':        primary = cmp.text(a.ip ?? '', b.ip ?? ''); break;
      }
      if (primary !== 0) return primary * factor;
      return cmp.date(b.createdAt, a.createdAt);
    });
  }, [data, sort]);

  return (
    <>
      <PageHeader title="Histórico de atividades" breadcrumb="Configurações" />
      <PageBody>
        <div className="bg-card border rounded-lg p-3 grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">O que aconteceu</Label>
            <Select
              value={filters.action}
              onChange={(e) => {
                setSkip(0);
                setFilters((f) => ({ ...f, action: e.target.value }));
              }}
            >
              <option value="">Todas as ações</option>
              {ACTION_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.actions.map((code) => (
                    <option key={code} value={code}>
                      {ACTION_LABELS[code] ?? code}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Onde aconteceu</Label>
            <Select
              value={filters.entity}
              onChange={(e) => {
                setSkip(0);
                setFilters((f) => ({ ...f, entity: e.target.value }));
              }}
            >
              <option value="">Todas as áreas</option>
              {ENTITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ action: '', entity: '' });
                setSkip(0);
              }}
            >
              Limpar filtros
            </Button>
          </div>
        </div>

        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="createdAt" label="Quando"        sort={sort} onClick={toggleSort} />
                <SortHeader column="user"      label="Quem"          sort={sort} onClick={toggleSort} />
                <SortHeader column="action"    label="O que fez"     sort={sort} onClick={toggleSort} />
                <SortHeader column="entity"    label="Onde"          sort={sort} onClick={toggleSort} />
                <SortHeader column="ip"        label="Origem (IP)"   sort={sort} onClick={toggleSort} />
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedItems.map((it) => (
                <tr key={it.id}>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {new Date(it.createdAt).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-4 py-2">
                    {it.user ? (
                      <>
                        <div>{it.user.name}</div>
                        <div className="text-xs text-muted-foreground">{it.user.email}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2" title={it.action}>
                    {actionLabel(it.action)}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {it.entity ? (
                      <>
                        <span title={it.entity}>{entityLabel(it.entity)}</span>
                        {it.entityId && (() => {
                          const summary = summarizeEntry(it);
                          const tooltip = summary
                            ? `${summary}\n\nID: ${it.entityId}`
                            : `ID: ${it.entityId}`;
                          return (
                            <span
                              className="text-xs ml-1 opacity-60 font-mono cursor-help underline decoration-dotted"
                              title={tooltip}
                            >
                              #{it.entityId.slice(0, 8)}
                            </span>
                          );
                        })()}
                      </>
                    ) : (() => {
                      // Sem entidade (ex.: LOGIN, maintenance.*) — usa metadata se houver.
                      const summary = summarizeEntry(it);
                      return summary ? (
                        <span className="cursor-help underline decoration-dotted" title={summary}>
                          detalhes
                        </span>
                      ) : (
                        '—'
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground font-mono text-xs">
                    {it.ip ?? '—'}
                  </td>
                </tr>
              ))}
              {!isFetching && (data?.items.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                    Nenhum registro com os filtros atuais
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data && data.total > take && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              {skip + 1}–{Math.min(skip + take, data.total)} de {data.total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - take))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={skip + take >= data.total}
                onClick={() => setSkip(skip + take)}
              >
                Próximo
              </Button>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
